import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} RecommendedProduct
 * @property {number} id
 * @property {string} title
 * @property {string} url
 * @property {string} featured_image
 * @property {number} price
 * @property {boolean} available
 * @property {Array<{id: number, available: boolean, title: string}>} variants
 * @property {Array<{id: number}>} [selling_plan_groups]
 */

class CartUpsellRecommendations extends HTMLElement {
  static ERROR_MESSAGE_DISPLAY_DURATION = 4000;
  static observedAttributes = ['data-product-id'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string} */
  #pendingUrl = '';

  /** @type {Promise<RecommendedProduct[]> | null} */
  #pendingFetch = null;

  /** @type {Record<string, RecommendedProduct[]>} */
  #cache = {};
  /** @type {number | null} */
  #errorTimeout = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);

    if (this.dataset.productId) this.#loadRecommendations();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-product-id' && oldValue !== null && newValue && newValue !== oldValue) {
      this.#loadRecommendations();
    }
  }

  disconnectedCallback() {
    this.#activeFetch?.abort();
    if (this.#errorTimeout) window.clearTimeout(this.#errorTimeout);
    this.removeEventListener('click', this.#handleClick);
  }

  load() {
    this.#loadRecommendations();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-upsell-add]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.dataset.needsModal === 'true') {
      this.#openQuickAdd(button);
    } else {
      this.#addToCart(button);
    }
  };

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    if (!variantId || button.disabled) return;

    this.#hideError();
    button.disabled = true;
    button.dataset.adding = 'true';

    try {
      const body = JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] });
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });

      if (!response.ok) {
        let message = '';
        try {
          const errorResponse = await response.json();
          message = errorResponse?.description || errorResponse?.message || '';
        } catch {
          // Ignore parsing errors and use fallback message below.
        }

        throw new Error(message || `HTTP ${response.status}`);
      }

      button.dataset.adding = 'false';
      button.dataset.added = 'true';

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-upsell',
          itemCount: 1,
          productId,
        })
      );

      setTimeout(() => {
        button.dataset.added = 'false';
        button.disabled = false;
      }, 1500);
    } catch (error) {
      const fallbackMessage = this.dataset.errorMessage?.trim() || 'Error adding to cart';
      const errorMessage =
        error instanceof Error && error.message && !error.message.startsWith('HTTP') ? error.message : fallbackMessage;
      this.#showError(errorMessage);
      button.dataset.adding = 'false';
      button.disabled = false;
    }
  }

  /** @param {HTMLButtonElement} button */
  async #openQuickAdd(button) {
    const productUrl = button.dataset.productUrl;
    if (!productUrl || button.disabled) return;

    button.disabled = true;
    button.dataset.adding = 'true';

    try {
      const quickAddComponent = /** @type {any} */ (document.querySelector('quick-add-component'));
      if (!quickAddComponent) return;

      const html = await quickAddComponent.fetchProductPage(productUrl);
      if (!html) return;

      const productGrid = html.querySelector('[data-product-grid-content]');
      if (!productGrid) return;

      const disclaimerEl = productGrid.querySelector('.non-eligibility-disclaimers');
      const disclaimerClone = disclaimerEl ? /** @type {Element} */ (disclaimerEl.cloneNode(true)) : null;

      const productGridClone = /** @type {Element} */ (productGrid.cloneNode(true));
      productGridClone.querySelector('.non-eligibility-disclaimers')?.remove();

      await quickAddComponent.updateQuickAddModal(productGridClone);

      if (disclaimerClone) {
        document.getElementById('quick-add-modal-content')?.appendChild(disclaimerClone);
      }

      const dialog = document.getElementById('quick-add-dialog');
      if (dialog && typeof /** @type {any} */ (dialog).showDialog === 'function') {
        /** @type {any} */ (dialog).showDialog();
      }
    } finally {
      button.dataset.adding = 'false';
      button.disabled = false;
    }
  }

  async #loadRecommendations() {
    const { productId, url } = this.dataset;
    if (!productId || !url) return;

    const fullUrl = `${url}&product_id=${productId}`;

    try {
      const products = await this.#fetchProducts(fullUrl);

      if (!products?.length) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.#render(products);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.hidden = true;
    }
  }

  /** @param {string} url */
  async #fetchProducts(url) {
    const cached = this.#cache[url];
    if (cached) return cached;

    if (this.#pendingUrl === url && this.#pendingFetch) {
      return this.#pendingFetch;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    this.#pendingUrl = url;

    const controller = this.#activeFetch;
    const promise = fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        /** @type {RecommendedProduct[]} */
        const products = data.products ?? [];
        this.#cache[url] = products;
        return products;
      })
      .finally(() => {
        if (this.#pendingFetch !== promise) return;
        this.#activeFetch = null;
        this.#pendingFetch = null;
        this.#pendingUrl = '';
      });

    this.#pendingFetch = promise;

    return this.#pendingFetch;
  }

  /** @param {RecommendedProduct[]} products */
  #render(products) {
    const cardTemplate = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-card]'));
    if (!cardTemplate) return;

    const heading = this.dataset.heading ?? '';
    const fragment = document.createDocumentFragment();

    const header = document.createElement('div');
    header.className = 'cart-upsell__header';

    if (heading) {
      const h2 = document.createElement('h2');
      h2.className = 'cart-upsell__heading';
      h2.textContent = heading;
      header.appendChild(h2);
    }

    const navTemplate = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-nav]'));
    if (navTemplate) header.appendChild(navTemplate.content.cloneNode(true));

    fragment.appendChild(header);

    const list = document.createElement('div');
    list.className = 'cart-upsell__list';

    for (const product of products) {
      const card = this.#buildCard(cardTemplate, product);
      if (card) list.appendChild(card);
    }

    fragment.appendChild(list);

    const skeleton = this.querySelector('[data-skeleton]');
    if (skeleton) skeleton.remove();

    const existing = this.querySelector('.cart-upsell__content');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'cart-upsell__content';
    wrapper.appendChild(fragment);
    this.appendChild(wrapper);

    requestAnimationFrame(() => wrapper.classList.add('is-visible'));

    this.#initNav(wrapper);
  }

  /** @param {HTMLElement} wrapper */
  #initNav(wrapper) {
    const list = wrapper.querySelector('.cart-upsell__list');
    const prev = wrapper.querySelector('[data-upsell-prev]');
    const next = wrapper.querySelector('[data-upsell-next]');

    if (!(list instanceof HTMLElement) || !prev || !next) return;

    const updateButtons = () => {
      const { scrollLeft, scrollWidth, clientWidth } = list;
      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      if (prev instanceof HTMLButtonElement) prev.disabled = atStart;
      if (next instanceof HTMLButtonElement) next.disabled = atEnd;
    };

    let isScrolling = false;

    const scrollBy = (direction = 1) => {
      if (isScrolling) return;
      const card = list.querySelector('.cart-upsell-card');
      const gap = Number.parseFloat(getComputedStyle(list).gap) || 10;
      const distance = card ? card.getBoundingClientRect().width + gap : 200;
      isScrolling = true;
      list.scrollBy({ left: distance * direction, behavior: 'smooth' });
      list.addEventListener('scrollend', () => { isScrolling = false; }, { once: true });
    };

    prev.addEventListener('click', () => scrollBy(-1));
    next.addEventListener('click', () => scrollBy(1));
    list.addEventListener('scroll', updateButtons, { passive: true });

    updateButtons();
  }

  /**
   * @param {HTMLTemplateElement} cardTemplate
   * @param {RecommendedProduct} product
   * @returns {DocumentFragment | null}
   */
  #buildCard(cardTemplate, product) {
    const clone = /** @type {DocumentFragment} */ (cardTemplate.content.cloneNode(true));
    const link = clone.querySelector('a');
    if (link) link.href = product.url;

    const img = clone.querySelector('[data-upsell-image]');

    if (img instanceof HTMLImageElement && product.featured_image) {
      const separator = product.featured_image.includes('?') ? '&' : '?';
      img.src = `${product.featured_image}${separator}width=160`;
      img.alt = product.title;
    }

    const title = clone.querySelector('[data-upsell-title]');
    if (title) title.textContent = product.title;

    const variantEl = /** @type {HTMLElement | null} */ (clone.querySelector('[data-upsell-variant]'));
    if (variantEl) {
      const firstVariant = product.variants?.[0];
      const variantTitle = firstVariant?.title;
      if (variantTitle && variantTitle !== 'Default Title') {
        variantEl.textContent = variantTitle;
        variantEl.hidden = false;
      }
    }

    const price = clone.querySelector('[data-upsell-price]');
    if (price) price.textContent = this.#formatPrice(product.price);

    const addButton = clone.querySelector('[data-upsell-add]');

    if (addButton instanceof HTMLButtonElement) {
      const addToCartLabel = addButton.getAttribute('aria-label');
      if (addToCartLabel) addButton.setAttribute('aria-label', `${addToCartLabel}: ${product.title}`);

      const needsModal = product.variants?.length > 1 || (product.selling_plan_groups?.length ?? 0) > 0;

      if (needsModal) {
        addButton.dataset.needsModal = 'true';
        addButton.dataset.productUrl = product.url;
        addButton.dataset.productId = String(product.id);
      } else {
        const variant = product.variants?.find((v) => v.available);

        if (variant) {
          addButton.dataset.variantId = String(variant.id);
          addButton.dataset.productId = String(product.id);
        } else {
          addButton.hidden = true;
        }
      }
    }

    return clone;
  }

  /** @param {number} subunit */
  #formatPrice(subunit) {
    const formatEl = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-money-format]'));
    if (!formatEl) return '';

    const pattern = formatEl.content.textContent?.trim() || '{{amount}}';
    const currency = this.dataset.currency?.trim() || 'USD';
    return formatMoney(subunit, pattern, currency);
  }

  /** @returns {HTMLElement | null} */
  #getErrorElement() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-upsell-error]'));
  }

  /** @param {string} message */
  #showError(message) {
    const errorEl = this.#getErrorElement();
    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.hidden = false;

    if (this.#errorTimeout) window.clearTimeout(this.#errorTimeout);
    this.#errorTimeout = window.setTimeout(() => this.#hideError(), CartUpsellRecommendations.ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #hideError() {
    const errorEl = this.#getErrorElement();
    if (!errorEl) return;

    errorEl.hidden = true;
    errorEl.textContent = '';
    if (this.#errorTimeout) {
      window.clearTimeout(this.#errorTimeout);
      this.#errorTimeout = null;
    }
  }
}

if (!customElements.get('cart-upsell-recommendations')) {
  customElements.define('cart-upsell-recommendations', CartUpsellRecommendations);
}
